import { Injectable, computed, inject, signal } from '@angular/core';
import { environment } from '@environments/environment';
import { Observable, of, shareReplay, tap } from 'rxjs';
import { HttpService } from './http.service';
import { CompanyModel } from '@models/company/company.model';

const STORAGE_KEY = 'company_cache';
const STORAGE_KEY_TS = 'company_cache_ts';
const CACHE_AGE = 60 * 1000 * 5; // 5 minutes

@Injectable({
  providedIn: 'root',
})
export class CompanyService {
  private readonly companiesApi = `${environment.caisseApiUrl}/companies/module/selfPosTerminal`;
  private readonly httpService = inject(HttpService);

  private readonly company = signal<CompanyModel | null>(this.#readStorage());
  private readonly companyLastUpdate = signal<number>(
    Number(localStorage.getItem(STORAGE_KEY_TS) ?? 0),
  );
  private inFlight$: Observable<CompanyModel | null> | null = null;

  readonly currentCompany = computed(() => this.company());

  getCompany(): Observable<CompanyModel | null> {
    if (this.companyLastUpdate() > Date.now() - CACHE_AGE) {
      return of(this.company()!);
    }
    return this.loadCompany();
  }

  loadCompany(): Observable<CompanyModel | null> {
    if (!this.inFlight$) {
      this.inFlight$ = this.httpService.get<CompanyModel>(this.companiesApi).pipe(
        tap((data) => {
          this.company.set(data);
          this.companyLastUpdate.set(Date.now());
          this.inFlight$ = null;
          localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
          localStorage.setItem(STORAGE_KEY_TS, String(Date.now()));
        }),
        shareReplay(1),
      );
    }
    return this.inFlight$;
  }

  #readStorage(): CompanyModel | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as CompanyModel) : null;
    } catch {
      return null;
    }
  }
}
